import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";

export interface ConversationField {
  key: string;
  label: string;
  placeholder: string;
}

export interface ConversationBlock {
  id: string;
  block_id: string;
  block_label: string;
  fields: ConversationField[];
  sort_order: number;
  is_active: boolean;
}

export function useConversationFieldSettings() {
  const [blocks, setBlocks] = useState<ConversationBlock[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const { data, error: fetchError } = await supabase
        .from("conversation_field_settings")
        .select("*")
        .order("sort_order");

      if (fetchError) throw fetchError;

      // Parse fields from JSONB and filter active blocks
      const parsedBlocks = (data || [])
        .filter((block) => block.is_active)
        .map((block) => ({
          ...block,
          fields: typeof block.fields === "string" 
            ? JSON.parse(block.fields) 
            : block.fields,
        }));

      setBlocks(parsedBlocks);
    } catch (err: any) {
      console.error("Error loading conversation field settings:", err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Get all field keys for initializing form data
  const getAllFieldKeys = (): string[] => {
    const keys: string[] = [];
    blocks.forEach((block) => {
      block.fields.forEach((field) => {
        keys.push(field.key);
      });
    });
    return keys;
  };

  // Get a flat list of all fields with their block info
  const getAllFields = (): Array<ConversationField & { blockId: string; blockLabel: string }> => {
    const fields: Array<ConversationField & { blockId: string; blockLabel: string }> = [];
    blocks.forEach((block) => {
      block.fields.forEach((field) => {
        fields.push({
          ...field,
          blockId: block.block_id,
          blockLabel: block.block_label,
        });
      });
    });
    return fields;
  };

  return {
    blocks,
    loading,
    error,
    getAllFieldKeys,
    getAllFields,
    reload: loadSettings,
  };
}
